/**
 * quasicrystalPipeline — Penrose-style aperiodic tiling.
 *
 * Penrose tilings use a few tile shapes (kite, dart) and a specific
 * matching rule that produces non-periodic patterns with 5-fold
 * symmetry. Here we approximate with a 2D quasi-periodic sum:
 *   p(x,y) = cos(2*pi*(a*x + b*y)) + cos(2*pi*(c*x + d*y)) + ...
 *
 * The result is a non-repeating pattern with subtle rotational
 * symmetry — never quite the same in any two regions.
 *
 * The classic Penrose uses 5 sets of parallel lines at 0°, 36°, 72°,
 * 108°, 144°. We sample a high-frequency noise pattern along these
 * line directions.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface QuasicrystalPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// Penrose tiling uses 5 line directions at 36° intervals.
// Each direction is parameterized as k_i = (cos(theta_i), sin(theta_i))
// for theta_i in {0, 36, 72, 108, 144} degrees.
// The tiling forms where these lines intersect.

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Slow rotation (audio drives speed).
  float angle = uTime * 0.1 * (0.5 + bass * 0.7);
  float ca = cos(angle), sa = sin(angle);
  vec2 rp = mat2(ca, -sa, sa, ca) * p;

  // 5 line directions (0°, 36°, 72°, 108°, 144°).
  // cos/sin of each angle.
  const float PI = 3.14159265;
  const float TWO_PI = 6.2831853;
  float scales[5];
  scales[0] = TWO_PI * 4.0;
  scales[1] = TWO_PI * 4.0;
  scales[2] = TWO_PI * 4.0;
  scales[3] = TWO_PI * 4.0;
  scales[4] = TWO_PI * 4.0;
  // The trick: sum cos of 5 line projections at different angles.
  // When all 5 are equal, the sum is highly directional and creates
  // Penrose-like patterns.
  float pattern = 0.0;
  float scaleBase = 6.0 + bass * 2.0;
  pattern += cos(scaleBase * (rp.x * cos(0.0) + rp.y * sin(0.0)));
  pattern += cos(scaleBase * (rp.x * cos(TWO_PI / 5.0) + rp.y * sin(TWO_PI / 5.0)));
  pattern += cos(scaleBase * (rp.x * cos(2.0 * TWO_PI / 5.0) + rp.y * sin(2.0 * TWO_PI / 5.0)));
  pattern += cos(scaleBase * (rp.x * cos(3.0 * TWO_PI / 5.0) + rp.y * sin(3.0 * TWO_PI / 5.0)));
  pattern += cos(scaleBase * (rp.x * cos(4.0 * TWO_PI / 5.0) + rp.y * sin(4.0 * TWO_PI / 5.0)));
  pattern = pattern / 5.0;  // normalize to [-1, 1]

  // Convert to a color: where pattern is near 0 (constructive
  // interference), we get the bright tile color. Near ±1 (destructive
  // interference) is the dark background.
  float tile = smoothstep(0.4, 0.95, abs(pattern));

  // Color palette: gold/amber Penrose tiles on dark teal background.
  vec3 bg = vec3(0.05, 0.10, 0.15);
  vec3 tileColor = vec3(1.0, 0.75, 0.30);
  // Audio: brighten tiles on high.
  tileColor *= 1.0 + high * 0.5;
  vec3 col = mix(bg, tileColor, 1.0 - tile);

  // Subtle radial gradient.
  float r = length(p);
  col *= 1.0 - r * r * 0.2;

  // Vignette.
  float vig = 1.0 - r * 0.4;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeQuasicrystalBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeQuasicrystalRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createQuasicrystalPipeline(gl: WebGL2RenderingContext): QuasicrystalPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawQuasicrystal(
  gl: WebGL2RenderingContext,
  pipe: QuasicrystalPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
